import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpParams} from "@angular/common/http";
import {BehaviorSubject, catchError, combineLatest, finalize, map, Observable, of, switchMap, tap} from "rxjs";
import {environment} from "../../environments/environment";
import {CountryService} from "./country.service";
import {NotificationService} from "./notification.service";
import {FeedInRate} from "../model/feed-in-rate";

@Injectable({
  providedIn: 'root'
})
export class FeedInRatesService {
  private readonly apiUrl = `${environment.backend.baseUrl}/feed-in-rate`;

  data$: Observable<FeedInRate[]>;

  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();

  // Trigger for reload
  private refreshTrigger$ = new BehaviorSubject<void>(undefined);

  constructor(
    private country: CountryService,
    private http: HttpClient,
    private notification: NotificationService
  ) {
    // Combine country selection AND trigger
    // If one changes --> reload.
    this.data$ = combineLatest([this.country.selected$, this.refreshTrigger$]).pipe(
      switchMap(([countryCode, _]) => {
        if (!countryCode) {
          return of([]);
        }

        const params = new HttpParams().set('countryCode', countryCode);
        this.loadingSubject.next(true);

        return this.http.get<FeedInRate[]>(this.apiUrl, {params}).pipe(
          catchError((err: HttpErrorResponse) => {
            // Only show error, if its not a 404
            if (err.status !== 404) {
              this.notification.error('Fehler beim Laden der Einspeisevergütungen');
            }
            return of([]); // Empty array
          }),
          finalize(() => this.loadingSubject.next(false))
        );
      })
    );
  }

  /**
   * Save a new entry.
   */
  saveFeedInRate(feedInRate: FeedInRate): Observable<FeedInRate> {
    this.loadingSubject.next(true);
    return this.http.put<FeedInRate>(this.apiUrl, feedInRate).pipe(
      tap(() => {
        this.reload();

      }),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Delete an entry
   */
  deleteFeedInRate(id: string): Observable<void> {
    this.loadingSubject.next(true);
    const url = `${this.apiUrl}/${id}`;
    return this.http.delete<void>(url).pipe(
      tap(() => {
        this.reload();
        this.notification.success("Eintrag gelöscht");
      }),
      catchError(err => {
        this.notification.error("Löschen fehlgeschlagen");
        throw err;
      }),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Helper method to reload manually
   */
  reload() {
    this.refreshTrigger$.next();
  }
}
